import { useMutation } from '@apollo/client';
import React, { useCallback } from 'react';
import { Form } from 'react-final-form';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { InputField } from '../../../components/FinalForm/InputField';
import { SubmitButtonV3 } from '../../../components/FinalForm/SubmitButtonV3';
import { TextAreaField } from '../../../components/FinalForm/TextAreaField';
import { confirmationToast, failureToast } from '../../../components/Toast';
import { ReactComponent as InformationIcon } from '../../../icons/information.svg';
import { isEmail, minLength } from '../../../utils/validation';
import { createOffenseMutation } from '../../api/offense';

const messages = defineMessages({
  title: { defaultMessage: 'Signaler une infraction', id: 'pF8RMS' },
  emailPlaceholder: { defaultMessage: 'Votre email', id: 'GmQRpf' },
  contentPlaceholder: {
    defaultMessage: `Précisez-nous ici l'infraction constatée, par exemple :
  - Racisme
  - Spam
  - Pédophilie
  - Pornographie
  - Suicide
  - Appel au meurtre
  - Respect des victimes
  - Injure sur religion
  - Homophobie
  - Incitation haine/violence/crime/vol`,
    id: 'VzdZWJ',
  },
  legalWarning: {
    defaultMessage:
      'NB: votre adresse IP sera enregistrée avec ce signalement. Tout signalement abusif pourra être sanctionné.',
    id: 'tL8FUg',
  },
  success: { defaultMessage: 'Signalement envoyé', id: 'jRLjQK' },
  error: { defaultMessage: 'Une erreur est survenue', id: 'uM26tE' },
});

const contentValidation = minLength(4);

const FORM_SUBSCRIPTION = {};

export type ReportCommentProps = {
  id: string;
  object: 'comment' | 'topic';
  controller?: { hide: () => void };
  className?: string;
};
export const ReportComment: React.FC<ReportCommentProps> = ({ id, object, controller }) => {
  const { formatMessage } = useIntl();
  const [createOffense] = useMutation(createOffenseMutation);

  const renderForm = useCallback(
    ({ handleSubmit }) => (
      <form onSubmit={handleSubmit} className="flex flex-col space-y-4 mt-6">
        <InputField
          placeholder={formatMessage(messages.emailPlaceholder)}
          name="email"
          required
          validate={isEmail}
        />
        <TextAreaField
          placeholder={formatMessage(messages.contentPlaceholder)}
          name="content"
          required
          validate={contentValidation}
          rows={12}
        />
        <div className="pt-1 pb-3 flex items-center space-x-3">
          <InformationIcon className="w-5 h-5" />{' '}
          <em className="flex-1 text-gray-550">
            <FormattedMessage {...messages.legalWarning} />
          </em>
        </div>
        <SubmitButtonV3 variant="contained">Envoyer</SubmitButtonV3>
      </form>
    ),
    [formatMessage],
  );

  const submit = useCallback(
    async values => {
      try {
        await createOffense({ variables: { ...values, id, object } });
        controller?.hide();
        confirmationToast(<FormattedMessage {...messages.success} />);
      } catch (error) {
        failureToast(<FormattedMessage {...messages.error} />);
      }
    },
    [id, object, controller, createOffense],
  );

  return (
    <div className="bg-white w-screen max-w-full">
      <div className="text-md font-bold">
        <FormattedMessage {...messages.title} />
      </div>
      <Form onSubmit={submit} subscription={FORM_SUBSCRIPTION} render={renderForm} />
    </div>
  );
};
